// Single truth for the operations home (Servis wave 2).
//
// The Worker DECIDES a restart is allowed and the console DRAWS the same
// decision. Both import these functions, so a button can never offer an
// action the server will refuse, and the server can never refuse something
// the operator was told was safe.
import type { DfyBuildStepDto } from "./dto";

/** A build that has not reported for this long is no longer "running".
 *
 * Deliberately the same window `launchDfyBuild` uses to decide a run still
 * occupies its gate (ACTIVE_RUN_CUTOFF_MS in worker/src/dfy-build.ts): the
 * moment the server stops treating a run as active is exactly the moment a
 * restart cannot cause double work. */
export const DFY_RUN_SILENT_MS = 10 * 60 * 1000;

/** Window in which the operations home keeps showing finished work, so a
 * restart that succeeded is visible on the same screen that started it. */
export const ADMIN_OPS_RECENT_MS = 24 * 60 * 60 * 1000;

export type AdminOpsRunStatus = "running" | "done" | "failed";

export type AdminOpsRunPhase =
  | "running"
  | "stalled"
  | "failed"
  | "awaiting_restart"
  | "restart_silent"
  | "done";

export type AdminOpsRestartBlock =
  | "running"
  | "awaiting_restart"
  | "finished"
  | "unknown_gate";

export interface AdminOpsRunDto {
  runId: string;
  clientId: string;
  clientName: string;
  clientEmail: string;
  isTest: boolean;
  /** Key the runner reports (strategy, plan, salespage, emails, webinar). */
  gateKey: string;
  /** Launch gate the restart would fire; null when the runner reported a key
   * this Worker cannot map, which is exactly when a restart must not guess. */
  gate: string | null;
  status: AdminOpsRunStatus;
  steps: DfyBuildStepDto[];
  startedAt: string;
  updatedAt: string;
  finishedAt: string | null;
  note: string | null;
  etaAt?: string;
  etaSampleSize?: number;
  /** Last admin restart of this client and gate, when it is newer than the
   * run's own last report. Null once the runner speaks again. */
  restartRequestedAt: string | null;
  canRestart: boolean;
  restartBlockedBy: AdminOpsRestartBlock | null;
}

export interface AdminOpsCallDto {
  id: string;
  kind: "onboarding" | "marketing";
  locale: "cs" | "en";
  startsAt: string;
  zoomUrl: string;
  capacity: number;
  enrolledCount: number;
  cancelledAt: string | null;
}

export interface AdminOpsSummary {
  running: number;
  stalled: number;
  failed: number;
  awaiting: number;
}

export interface AdminOpsResponse {
  ok: true;
  runs: AdminOpsRunDto[];
  recent: AdminOpsRunDto[];
  calls: AdminOpsCallDto[];
  summary: AdminOpsSummary;
  /** False when DFYBUILD_TRIGGER_URL is unset: every restart would fail, so
   * the console says so instead of offering a dead button. */
  restartAvailable: boolean;
  generatedAt: string;
}

export type AdminOpsRestartOutcome = "launched" | "skipped";
export type AdminOpsRestartSkip = "deliverable_exists" | "run_active" | "command_replayed";

export interface AdminOpsRestartResult {
  ok: true;
  outcome: AdminOpsRestartOutcome;
  reason: AdminOpsRestartSkip | null;
  clientId: string;
  clientName: string;
  gateKey: string;
  gate: string;
  requestedAt: string;
}

export type AdminOpsRestartRefusal =
  | "run_moved"
  | "run_active"
  | "awaiting_restart"
  | "finished"
  | "unknown_gate"
  | "not_ready"
  | "prerequisite_missing"
  | "trigger_unavailable";

type RunFacts = Pick<AdminOpsRunDto, "status" | "updatedAt" | "gate" | "restartRequestedAt">;

function elapsedSince(value: string | null, nowMs: number): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? nowMs - parsed : null;
}

/** What the operator is actually looking at. A restart that has been fired
 * but not yet acknowledged is its own state: reporting it as "failed" would
 * invite a second launch of the same build. */
export function dfyRunPhase(run: RunFacts, nowMs: number): AdminOpsRunPhase {
  const sinceRestart = elapsedSince(run.restartRequestedAt, nowMs);
  const restartIsNewer = run.restartRequestedAt !== null && run.updatedAt < run.restartRequestedAt;
  if (restartIsNewer && sinceRestart !== null) {
    return sinceRestart <= DFY_RUN_SILENT_MS ? "awaiting_restart" : "restart_silent";
  }
  if (run.status === "failed") return "failed";
  if (run.status === "running") {
    const silent = elapsedSince(run.updatedAt, nowMs);
    return silent === null || silent > DFY_RUN_SILENT_MS ? "stalled" : "running";
  }
  return "done";
}

export function adminOpsRestartCheck(run: RunFacts, nowMs: number): {
  canRestart: boolean;
  blockedBy: AdminOpsRestartBlock | null;
} {
  if (!run.gate) return { canRestart: false, blockedBy: "unknown_gate" };
  const phase = dfyRunPhase(run, nowMs);
  if (phase === "running") return { canRestart: false, blockedBy: "running" };
  if (phase === "awaiting_restart") return { canRestart: false, blockedBy: "awaiting_restart" };
  if (phase === "done") return { canRestart: false, blockedBy: "finished" };
  return { canRestart: true, blockedBy: null };
}

export function adminOpsSummary(runs: readonly RunFacts[], nowMs: number): AdminOpsSummary {
  const summary: AdminOpsSummary = { running: 0, stalled: 0, failed: 0, awaiting: 0 };
  for (const run of runs) {
    const phase = dfyRunPhase(run, nowMs);
    if (phase === "running") summary.running += 1;
    else if (phase === "stalled" || phase === "restart_silent") summary.stalled += 1;
    else if (phase === "failed") summary.failed += 1;
    else if (phase === "awaiting_restart") summary.awaiting += 1;
  }
  return summary;
}

/** Oldest trouble first: a build that died an hour ago outranks one that died
 * a minute ago, and live work never pushes a failure off the top. */
export function adminOpsRunOrder(left: AdminOpsRunDto, right: AdminOpsRunDto, nowMs: number): number {
  const weight: Record<AdminOpsRunPhase, number> = {
    failed: 0,
    restart_silent: 1,
    stalled: 2,
    awaiting_restart: 3,
    running: 4,
    done: 5
  };
  const delta = weight[dfyRunPhase(left, nowMs)] - weight[dfyRunPhase(right, nowMs)];
  if (delta !== 0) return delta;
  return Date.parse(left.updatedAt) - Date.parse(right.updatedAt);
}
